import { getCompany } from "../data/storage/authStorage";
import type { Currency } from "../../services/api";

/** Company base currency code (uppercase), from localStorage company profile. */
export function getCompanyCurrencyCodeUpper(): string {
  const c = getCompany();
  return String(c?.currency || "").trim().toUpperCase();
}

/** True when document currency differs from company base — show FX row. */
export function isDocumentFxMismatch(documentCurrencyCode: string): boolean {
  const company = getCompanyCurrencyCodeUpper();
  const doc = String(documentCurrencyCode || "").trim().toUpperCase();
  return Boolean(company && doc && company !== doc);
}

/** Resolve `currency_code` / `currency_symbol` from catalog using `currency_id` only. */
export function resolveCurrencyFieldsFromCatalog(
  currencies: Currency[],
  currencyId: string,
): {
  currency_id: string;
  currency_code: string;
  currency_symbol: string;
} | null {
  const id = String(currencyId ?? "").trim();
  if (!id || currencies.length === 0) return null;
  const hit = currencies.find((c) => String(c.currency_id).trim() === id);
  if (!hit) return null;
  return {
    currency_id: hit.currency_id,
    currency_code: hit.currency_code,
    currency_symbol: hit.currency_symbol,
  };
}

/** Resolve full currency triple from catalog by ISO code. */
export function resolveCurrencyFieldsByCode(
  currencies: Currency[],
  currencyCode: string,
): {
  currency_id: string;
  currency_code: string;
  currency_symbol: string;
} | null {
  const code = String(currencyCode ?? "").trim().toUpperCase();
  if (!code || currencies.length === 0) return null;
  const hit = currencies.find(
    (c) => String(c.currency_code).trim().toUpperCase() === code,
  );
  if (!hit) return null;
  return {
    currency_id: hit.currency_id,
    currency_code: hit.currency_code,
    currency_symbol: hit.currency_symbol,
  };
}

export type ResolvedDocumentCurrency = {
  currency_id: string;
  currency_code: string;
  currency_symbol: string;
  /** Null only when FX is needed, no rate was given, and the caller allows it. */
  exchange_rate: number | null;
};

/**
 * Resolve document currency at submit time from id and/or code plus optional FX input.
 * Document currency is independent of company base currency.
 *
 * `requireExchangeRate` false lets a foreign-currency document through without a
 * rate (Zoho fills in the latest one); true fails so the caller can surface an
 * error, which is what Tally needs since it has no rate lookup of its own.
 */
export function resolveDocumentCurrencyFields(
  currencies: Currency[],
  partial: {
    currency_id?: string;
    currency_code?: string;
    currency_symbol?: string;
    exchange_rate?: number | null;
  },
  exchangeRateInput: string,
  companyCurrencyCode?: string,
  requireExchangeRate = true,
): ResolvedDocumentCurrency | null {
  const companyCode = (
    companyCurrencyCode ?? getCompanyCurrencyCodeUpper()
  ).trim();

  let resolved: {
    currency_id: string;
    currency_code: string;
    currency_symbol: string;
  } | null = null;

  const id = String(partial.currency_id ?? "").trim();
  const code = String(partial.currency_code ?? "").trim();
  const symbol = String(partial.currency_symbol ?? "").trim();

  if (id) {
    resolved = resolveCurrencyFieldsFromCatalog(currencies, id);
  }
  if (!resolved && code) {
    resolved = resolveCurrencyFieldsByCode(currencies, code);
  }
  if (!resolved && id && code && symbol) {
    resolved = {
      currency_id: id,
      currency_code: code,
      currency_symbol: symbol,
    };
  }
  if (!resolved) return null;

  const docCode = resolved.currency_code.trim().toUpperCase();
  const needsFx =
    Boolean(companyCode) && Boolean(docCode) && companyCode !== docCode;

  let exchange_rate: number | null;
  if (needsFx) {
    const fromInput = parseExchangeRateInputForPayload(exchangeRateInput);
    if (fromInput != null && fromInput > 0) {
      exchange_rate = fromInput;
    } else if (
      partial.exchange_rate != null &&
      Number(partial.exchange_rate) > 0
    ) {
      exchange_rate = Number(partial.exchange_rate);
    } else if (requireExchangeRate) {
      return null;
    } else {
      exchange_rate = null;
    }
  } else {
    exchange_rate = 1;
  }

  return { ...resolved, exchange_rate };
}

/** Payload value for `exchange_rate`: empty input → `null`. */
export function parseExchangeRateInputForPayload(input: string): number | null {
  const t = input.trim();
  if (t === "") return null;
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : null;
}
